import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../shared/auth/AuthContext.jsx';
import OrbitModuleHeader from '../../shared/hub/OrbitModuleHeader.jsx';
import { GROUPS } from '../../shared/modules/registry.js';
import { T } from '../../shared/styles/tokens.js';
import UserAccountDialog from './UserAccountDialog.jsx';
import './user-management.css';

import { API_BASE } from '../../shared/http/apiBase.js';

const ROLES = ['admin', 'user'];

// An administrator reaches everything by virtue of users.role, so a grant row would change
// nothing while looking as though it had. The server refuses the write; this says why before
// anyone tries.
const ADMIN_NOTE =
  'Administrators can open every tool and every client. There is nothing to grant — ' +
  'switch the role to "user" to choose individual tools.';

// ── password generator ────────────────────────────────────────────────────────

function genPassword(len = 16) {
  const upper  = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower  = 'abcdefghjkmnpqrstuvwxyz';
  const digits = '23456789';
  const special = '!@#$%^&*';
  const all = upper + lower + digits + special;
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  const chars = [
    upper [arr[0] % upper.length],
    lower [arr[1] % lower.length],
    digits[arr[2] % digits.length],
    special[arr[3] % special.length],
    ...Array.from({ length: len - 4 }, (_, i) => all[arr[i + 4] % all.length]),
  ];
  // shuffle
  for (let i = chars.length - 1; i > 0; i--) {
    const j = arr[i % arr.length] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

// ── api helpers ───────────────────────────────────────────────────────────────

function authHeaders(token) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

// ── copy button ───────────────────────────────────────────────────────────────

function CopyBtn({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  return (
    <button onClick={copy} className="o-btn">
      {copied ? '✓ Copied' : label}
    </button>
  );
}

// ── modal ─────────────────────────────────────────────────────────────────────

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="o-scrim" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="o-modal" style={wide ? s.accessModal : undefined}>
        <div className="o-modal-head">
          <h2>{title}</h2>
          <button onClick={onClose} style={s.closeBtn}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── access editor ─────────────────────────────────────────────────────────────
//
// One control for both halves of a grant: which tools the user sees on the Hub, and — for a
// client-scoped tool — which clients they get inside it. The two are nested rather than listed
// side by side because a client grant only means anything under a module grant: Atlantis in
// Taxonomy Generator and Atlantis in Competitive Monitoring are separate decisions, and a flat
// client list could not express one without the other.
//
// `value` is { modules: { id: level }, clients: { id: [clientId] } } — exactly the PUT body.

const EMPTY_ACCESS = { modules: {}, clients: {} };

function defaultLevel(m) {
  return m.levels.includes('viewer') ? 'viewer' : m.levels[0];
}

// Each tool names its own levels (registry `levelNames`). Falling back to the raw value rather
// than to a made-up label keeps a backend that predates the field readable instead of wrong.
function levelLabel(m, level) {
  return m.levelNames?.[level]?.label || level;
}

function levelNote(m, level) {
  return m.levelNames?.[level || defaultLevel(m)]?.note || '';
}

const LEVEL_ORDER = { viewer: 0, editor: 1, admin: 2 };

// Mirrors modules.grants_all_clients on the backend. A module can declare a level that reaches
// every client it is enabled for — the Taxonomy Generator's Super Admin, which that tool has
// always treated as full access in its own code. There is no per-client decision left to make at
// such a level, so the picker below is not disabled but absent: a disabled list of ticks reads
// as "these are the ones they have", which is the misreading this whole change is about.
function grantsAllClients(m, level) {
  if (!m?.clientScoped || !m.allClientsAtLevel || !level) return false;
  return (LEVEL_ORDER[level] ?? -1) >= (LEVEL_ORDER[m.allClientsAtLevel] ?? 99);
}

// ── directory search ──────────────────────────────────────────────────────────
//
// The Publicis directory (Entra ID, via Graph on the backend), searched as the admin
// types, so an account is created from the person's directory record instead of retyped.
// Renders nothing when the environment has no Graph credential — the probe on mount 503s
// and the manual fields below are the whole form, same opt-out convention as SSO.
//
// Hidden everywhere until the Graph User.Read.All grant is approved (requested
// 2026-08-31): gating at the call site also skips the probe request. Flip to true
// once the grant lands.
const DIRECTORY_SEARCH_ENABLED = false;

function DirectorySearch({ token, users, onPick }) {
  const [enabled, setEnabled] = useState(null);   // null = probe in flight
  const [q, setQ]             = useState('');
  const [results, setResults] = useState(null);   // null = no completed search to show
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const timer = useRef(null);
  const seq   = useRef(0);                        // drops out-of-order responses

  useEffect(() => {
    fetch(`${API_BASE}/portal/entra/users?q=`, { headers: authHeaders(token) })
      .then(r => setEnabled(r.ok))
      .catch(() => setEnabled(false));
    return () => clearTimeout(timer.current);
  }, [token]);

  function search(text) {
    setQ(text);
    clearTimeout(timer.current);
    const needle = text.trim();
    if (needle.length < 2) { setResults(null); setError(''); setBusy(false); return; }
    setBusy(true);
    timer.current = setTimeout(async () => {
      const mine = ++seq.current;
      try {
        const r = await fetch(
          `${API_BASE}/portal/entra/users?q=${encodeURIComponent(needle)}`,
          { headers: authHeaders(token) });
        const d = await r.json();
        if (mine !== seq.current) return;
        if (!r.ok) { setError(d.error || 'Directory search failed'); setResults([]); }
        else       { setError(''); setResults(d.users); }
      } catch {
        if (mine !== seq.current) return;
        setError('Network error'); setResults([]);
      } finally {
        if (mine === seq.current) setBusy(false);
      }
    }, 300);
  }

  // Someone already in Orbit is shown but not pickable — the search doubles as the
  // "do they already have an account?" check that otherwise ends in a 409.
  const orbitEmails = new Set((users || []).map(u => (u.email || '').toLowerCase()));

  function pick(person) {
    onPick({
      // The UPN's local part is the natural username for an account whose owner will
      // sign in with Microsoft anyway; it stays editable above.
      username:  (person.userPrincipalName.split('@')[0] || '').toLowerCase(),
      email:     person.email,
      full_name: person.displayName,
    });
    setQ(person.displayName);
    setResults(null);
  }

  if (!enabled) return null;

  return (
    <div style={s.fieldWrap}>
      <label className="o-field-lbl" style={s.compactLbl}>
        Publicis directory{busy && ' — searching…'}
      </label>
      <input
        className="o-control"
        placeholder="Search by name or email to prefill the fields below"
        value={q}
        onChange={e => search(e.target.value)}
      />
      {error && <p className="o-note" style={{ margin: `${T.s1}px 0 0` }}>{error}</p>}
      {results && !error && (
        results.length === 0
          ? <p className="o-note" style={{ margin: `${T.s1}px 0 0` }}>
              No one in the directory matches.
            </p>
          : <div className="o-popover" style={s.dirList}>
              {results.map(person => {
                const taken = orbitEmails.has(person.email.toLowerCase());
                return (
                  <button key={person.id} type="button" disabled={taken}
                          style={taken ? s.dirTaken : undefined}
                          onClick={() => pick(person)}>
                    {person.displayName}
                    <span style={{ color: T.text3 }}>
                      {' · '}{person.email}
                      {person.jobTitle && ` · ${person.jobTitle}`}
                    </span>
                    {taken && <span className="o-badge o-grey" style={s.dirBadge}>has account</span>}
                  </button>
                );
              })}
            </div>
      )}
    </div>
  );
}

// ── add user modal ────────────────────────────────────────────────────────────

function AddUserModal({ token, users, catalog, onClose, onCreated }) {
  const [form, setForm]       = useState({ username: '', email: '', full_name: '', role: 'user', password: genPassword() });
  const [access, setAccess]   = useState(EMPTY_ACCESS);
  const [selected, setSel]    = useState(null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const [created, setCreated] = useState(null);

  function field(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      // Grants ride along with the creation rather than following it, so a new user is never
      // briefly an account with no tools — and the admin never has to remember a second step.
      const body = form.role === 'admin' ? form : { ...form, ...access };
      const r = await fetch(`${API_BASE}/portal/users`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed to create user'); return; }
      setCreated({ user: d.user, password: form.password });
      onCreated(d.user);
    } catch {
      setError('Network error');
    } finally {
      setBusy(false);
    }
  }

  if (created) return (
    <Modal title="User Created" onClose={onClose}>
      <div className="o-modal-body">
        <div style={s.successBox}>
          <div style={s.successLine}>
            <span style={s.successLabel}>Username</span>
            <span style={s.successValue}>{created.user.username}</span>
          </div>
          <div style={s.successLine}>
            <span style={s.successLabel}>Email</span>
            <span style={s.successValue}>{created.user.email}</span>
          </div>
          <div style={s.successLine}>
            <span style={s.successLabel}>Password</span>
            <span style={{ ...s.successValue, fontFamily: 'monospace', fontWeight: 700 }}>{created.password}</span>
          </div>
        </div>
        <p style={s.shareNote}>Copy and share these credentials now — the password won't be shown again.</p>
      </div>
      <div className="o-modal-foot">
        <CopyBtn text={`Username: ${created.user.username}\nPassword: ${created.password}`} label="Copy credentials" />
        <button onClick={onClose} className="o-btn o-btn-primary">Done</button>
      </div>
    </Modal>
  );

  return (
    <Modal title="Add user" onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="o-modal-body" style={s.addBody}>
          {/* The identity fields are five short inputs and one decision; the grant below them is
              the part that needs room. `.o-ctrl-row-tight` is the existing compact control size,
              so the top of the form gives its height back to the tool list. */}
          <div className="o-ctrl-row-tight" style={s.addFields}>
            {DIRECTORY_SEARCH_ENABLED && (
              <DirectorySearch
                token={token} users={users}
                onPick={p => setForm(f => ({ ...f, ...p }))}
              />
            )}
            <div style={s.grid2}>
              <Field label="Username" value={form.username}  onChange={v => field('username', v)}  required compact />
              <Field label="Email"    value={form.email}     onChange={v => field('email', v)}     required type="email" compact />
            </div>
            <div style={s.grid2}>
              <Field label="Full name" value={form.full_name} onChange={v => field('full_name', v)} compact />
              <div style={s.fieldWrap}>
                <label className="o-field-lbl" style={s.compactLbl}>Role</label>
                <select value={form.role} onChange={e => field('role', e.target.value)} className="o-control">
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
            <div style={s.fieldWrap}>
              <label className="o-field-lbl" style={s.compactLbl}>Password</label>
              <div style={s.pwRow}>
                <input
                  className="o-control" style={{ fontFamily: T.fontMono, flex: 1 }}
                  value={form.password}
                  onChange={e => field('password', e.target.value)}
                  required
                />
                <button type="button" onClick={() => field('password', genPassword())} className="o-btn o-btn-sm">Regenerate</button>
                <CopyBtn text={form.password} />
              </div>
            </div>
          </div>

          <div style={s.toolsField}>
            <div style={s.toolsHead}>
              <span className="o-field-lbl" style={{ margin: 0 }}>Tools and clients</span>
              {form.role !== 'admin' && (
                <CopyFromUser
                  users={(users || []).filter(u => u.role !== 'admin')}
                  onPick={u => setAccess({ modules: u.modules || {}, clients: u.clients || {} })}
                />
              )}
            </div>
            {form.role === 'admin' ? (
              <p className="o-hint" style={{ margin: 0 }}><b>Full access.</b> {ADMIN_NOTE}</p>
            ) : (
              <>
                <AccessPanel
                  catalog={catalog} value={access} onChange={setAccess} embedded
                  selected={selected} onSelect={setSel}
                />
                {Object.keys(access.modules).length === 0 && (
                  <p className="o-hint" style={{ margin: `${T.s3}px 0 0` }}>
                    <b>Nothing selected.</b> The Hub will be empty for this user until you
                    share a tool. You can change this later.
                  </p>
                )}
              </>
            )}
          </div>

          {error && <div style={s.errorBox}>{error}</div>}
        </div>
        <div className="o-modal-foot">
          <button type="button" onClick={onClose} className="o-btn">Cancel</button>
          <button type="submit" className="o-btn o-btn-primary" disabled={busy}>{busy ? 'Creating…' : 'Create user'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── reset password modal ──────────────────────────────────────────────────────

function ResetModal({ token, user, onClose }) {
  const [password, setPassword] = useState(genPassword());
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/portal/users/${user.id}/reset`, {
        method: 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ password }),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Reset failed'); return; }
      setDone(true);
    } catch {
      setError('Network error');
    } finally {
      setBusy(false);
    }
  }

  if (done) return (
    <Modal title="Password Reset" onClose={onClose}>
      <div className="o-modal-body">
        <div style={s.successBox}>
          <div style={s.successLine}>
            <span style={s.successLabel}>Username</span>
            <span style={s.successValue}>{user.username}</span>
          </div>
          <div style={s.successLine}>
            <span style={s.successLabel}>New password</span>
            <span style={{ ...s.successValue, fontFamily: 'monospace', fontWeight: 700 }}>{password}</span>
          </div>
        </div>
        <p style={s.shareNote}>Copy and share these credentials now — the password won't be shown again.</p>
      </div>
      <div className="o-modal-foot">
        <CopyBtn text={`Username: ${user.username}\nPassword: ${password}`} label="Copy credentials" />
        <button onClick={onClose} className="o-btn o-btn-primary">Done</button>
      </div>
    </Modal>
  );

  return (
    <Modal title={`Reset password — ${user.username}`} onClose={onClose}>
      <form onSubmit={submit}>
        <div className="o-modal-body">
          <div style={s.fieldWrap}>
            <label className="o-field-lbl">New password</label>
            <div style={s.pwRow}>
              <input
                className="o-control" style={{ fontFamily: T.fontMono, flex: 1 }}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
              />
              <button type="button" onClick={() => setPassword(genPassword())} className="o-btn">Regenerate</button>
              <CopyBtn text={password} />
            </div>
          </div>
          {error && <div style={s.errorBox}>{error}</div>}
        </div>
        <div className="o-modal-foot">
          <button type="button" onClick={onClose} className="o-btn">Cancel</button>
          <button type="submit" className="o-btn o-btn-primary" disabled={busy}>{busy ? 'Resetting…' : 'Reset password'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── access panel (two-pane) ───────────────────────────────────────────────────
//
// The flat list this replaced put every tool's client picker on screen at once, so the tools a
// user actually holds were separated by hundreds of pixels of clients they don't. Splitting it
// keeps the whole grant readable down the left — ticked tools, their level, their client count —
// while the right pane carries only the one tool being edited.

function initials(u) {
  const src = (u.full_name || u.username || '').trim();
  if (!src) return '?';
  return src.split(/\s+/).slice(0, 2).map(p => p[0]).join('').toUpperCase();
}

function CopyFromUser({ users, onPick }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState('');
  // Anchored to the viewport rather than the button, because in the Add-user form this sits
  // inside the scrolling modal body — an absolutely positioned sheet there is clipped at the
  // body's edge, which is most of the sheet.
  const [at, setAt]     = useState(null);
  const btnRef = useRef(null);

  function toggle() {
    if (open) { setOpen(false); return; }
    const r = btnRef.current.getBoundingClientRect();
    setAt({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
    setOpen(true);
  }

  const matches = users.filter(u =>
    !q || `${u.username} ${u.full_name || ''} ${u.email || ''}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <>
      <button type="button" className="o-btn o-btn-sm" ref={btnRef} onClick={toggle}>
        Copy from user
      </button>
      {open && (
        <>
          {/* Click-away without a document listener: the sheet is the hit target. */}
          <div style={s.copyAway} onClick={() => setOpen(false)} />
          <div className="o-popover" style={{ ...s.copyPop, ...at }}>
            <input
              className="o-control" style={{ fontSize: T.tSmall, marginBottom: T.s1 }}
              placeholder="Search users" value={q} onChange={e => setQ(e.target.value)}
            />
            <div style={s.copyList}>
              {matches.length === 0
                ? <p className="o-note" style={{ margin: `${T.s2}px 11px` }}>No match.</p>
                : matches.map(u => (
                  <button key={u.id} type="button"
                    onClick={() => { onPick(u); setOpen(false); }}>
                    {u.username}
                    <span style={{ color: T.text3 }}>
                      {' · '}{u.role === 'admin' ? 'all tools'
                        : `${Object.keys(u.modules || {}).length} tools`}
                    </span>
                  </button>
                ))}
            </div>
            <p className="o-note" style={{ margin: `${T.s2}px 11px 4px` }}>
              Copies their tools and clients into this form. Nothing is saved until you save.
            </p>
          </div>
        </>
      )}
    </>
  );
}

// `embedded` is the same split inside a form rather than filling a modal: it takes a bounded
// height and a border of its own, so the tools section reads as one field among the others
// instead of running off the end of the body.
function AccessPanel({ catalog, value, onChange, selected, onSelect, embedded }) {
  const [q, setQ] = useState('');

  const modules = catalog?.modules || [];
  const clients = catalog?.clients || [];

  if (!modules.length) return (
    <div className="o-state" style={embedded ? undefined : { margin: T.s5 }}>
      Tool list unavailable. Reload the page; if it persists, the backend is still deploying.
    </div>
  );

  const grantable = catalog?.grantableModules ?? null;
  const grants = value.modules || {};
  const picks  = value.clients || {};

  const sel        = modules.find(m => m.id === selected) || modules[0];
  const selOn      = sel && grants[sel.id] !== undefined;
  const selLocked  = sel && grantable !== null && !grantable.includes(sel.id);
  const selOptions = sel ? clients.filter(c => c.modules?.[sel.id]) : [];
  const selChosen  = (sel && picks[sel.id]) || [];
  const selAll     = sel && selOn && grantsAllClients(sel, grants[sel.id]);
  const shown      = selOptions.filter(c =>
    !q || c.name.toLowerCase().includes(q.toLowerCase()));

  function toggle(m, on) {
    const next = { ...grants };
    // Client ticks survive an untick, so changing your mind costs nothing. The server drops
    // any that are still unmatched at save time.
    if (on) next[m.id] = next[m.id] || defaultLevel(m);
    else delete next[m.id];
    onChange({ modules: next, clients: picks });
    onSelect(m.id);
  }

  const setClients = ids => onChange({ modules: grants, clients: { ...picks, [sel.id]: ids } });

  return (
    <div style={embedded ? { ...s.split, ...s.splitEmbedded } : s.split}>
      {/* ── left: every tool, its grant state visible at a glance ── */}
      <div style={s.rail}>
        {GROUPS.map(g => {
          const rows = modules.filter(m => m.group === g.id);
          if (!rows.length) return null;
          return (
            <div key={g.id} style={{ marginBottom: T.s4 }}>
              <span className="o-eyebrow" style={{ display: 'block', padding: `0 11px ${T.s2}px` }}>
                {g.title}
              </span>
              {rows.map(m => {
                const on     = grants[m.id] !== undefined;
                const locked = grantable !== null && !grantable.includes(m.id);
                const opts   = clients.filter(c => c.modules?.[m.id]);
                const chosen = picks[m.id] || [];
                const active = sel?.id === m.id;
                return (
                  <div key={m.id} style={s.railRow}>
                    <input
                      type="checkbox" checked={on} disabled={locked} style={s.railBox}
                      aria-label={`Share ${m.label}`}
                      onChange={e => toggle(m, e.target.checked)}
                    />
                    <button
                      type="button" className="o-side-item" style={s.railBtn}
                      aria-current={active ? 'true' : undefined}
                      onClick={() => onSelect(m.id)}
                    >
                      <span style={{ minWidth: 0 }}>
                        <span style={s.railName}>{m.label}</span>
                        {on && (
                          <span style={s.railSub}>
                            {levelLabel(m, grants[m.id])}
                            {m.clientScoped && (grantsAllClients(m, grants[m.id])
                              ? ` · all ${opts.length} clients`
                              : ` · ${chosen.length} of ${opts.length} clients`)}
                          </span>
                        )}
                      </span>
                      {active && <span aria-hidden="true" style={s.chev}>›</span>}
                    </button>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* ── right: the one tool being edited ── */}
      <div style={s.detail}>
        {!sel ? null : (
          <>
            <h3 style={s.detailTitle}>{sel.label}</h3>

            {selLocked && (
              <p className="o-hint" style={{ margin: 0 }}>
                <b>Not yours to share.</b> You can grant access only to the tools you
                administer. Ask a global administrator for this one.
              </p>
            )}

            {!selOn && !selLocked && (
              <p className="o-hint" style={{ margin: 0 }}>
                <b>Not shared.</b> Tick {sel.label} in the list to grant it — the role and
                clients below take effect once you do.
              </p>
            )}

            <div style={s.roleRow}>
              <label className="o-field-lbl" htmlFor="acc-role" style={{ margin: 0 }}>Role</label>
              <select
                id="acc-role" className="o-control"
                value={grants[sel.id] || defaultLevel(sel)}
                disabled={!selOn || selLocked}
                onChange={e => {
                  // Ticks are dropped rather than kept on the way up to an all-clients level:
                  // the server stores none for it, so keeping them would show a change pending
                  // that will not be saved, and leave the summary counting clients nobody
                  // granted. Coming back down starts from none, which is the safe direction.
                  const next = { ...picks };
                  if (grantsAllClients(sel, e.target.value)) delete next[sel.id];
                  onChange({ modules: { ...grants, [sel.id]: e.target.value }, clients: next });
                }}
              >
                {/* The tool's own word for the level, not the platform's. `editor` in the
                    Taxonomy Generator is the role that tool calls Admin; showing "editor"
                    here is what sent an administrator into that tool to set the real role.
                    The stored value is unchanged. */}
                {sel.levels.map(l => <option key={l} value={l}>{levelLabel(sel, l)}</option>)}
              </select>
            </div>

            {levelNote(sel, grants[sel.id]) && (
              <p className="o-note" style={{ margin: 0 }}>{levelNote(sel, grants[sel.id])}</p>
            )}

            {sel.clientScoped && selAll ? (
              <p className="o-hint" style={{ margin: 0 }}>
                <b>Every client, automatically.</b> {levelLabel(sel, grants[sel.id])} in{' '}
                {sel.label} reaches all {selOptions.length} client
                {selOptions.length === 1 ? '' : 's'} enabled for it, including any enabled
                later. There is nothing to tick — lower the role to choose individual clients.
              </p>
            ) : sel.clientScoped && (selOptions.length === 0 ? (
              <p className="o-hint" style={{ margin: 0 }}>
                <b>No clients are enabled for {sel.label}.</b> Turn one on in Client
                Management, then come back to share it.
              </p>
            ) : (
              <>
                <input
                  className="o-control" value={q} onChange={e => setQ(e.target.value)}
                  placeholder={`Search ${selOptions.length} client${selOptions.length === 1 ? '' : 's'}`}
                  disabled={!selOn || selLocked}
                />
                <div style={s.clientHead}>
                  <span className="o-note">
                    {selChosen.length} of {selOptions.length} clients selected
                  </span>
                  <span>
                    <button type="button" className="o-btn o-btn-sm o-btn-ghost"
                      disabled={!selOn || selLocked}
                      onClick={() => setClients(selOptions.map(c => c.id))}>Select all</button>
                    <button type="button" className="o-btn o-btn-sm o-btn-ghost"
                      disabled={!selOn || selLocked}
                      onClick={() => setClients([])}>Clear</button>
                  </span>
                </div>

                <div style={s.clientList}>
                  {shown.length === 0 ? (
                    <p className="o-note" style={{ margin: 0 }}>No client matches “{q}”.</p>
                  ) : shown.map(c => (
                    <label key={c.id} className="o-check" style={s.clientRow}>
                      <input
                        type="checkbox" checked={selChosen.includes(c.id)}
                        disabled={!selOn || selLocked}
                        onChange={e => setClients(e.target.checked
                          ? [...selChosen, c.id]
                          : selChosen.filter(x => x !== c.id))}
                      />
                      {c.name}
                    </label>
                  ))}
                </div>

                {selOn && selChosen.length === 0 && (
                  <p className="o-hint" style={{ margin: 0 }}>
                    <b>No clients selected.</b> {sel.label} will open with an empty workspace
                    selector.
                  </p>
                )}
              </>
            ))}
          </>
        )}
      </div>
    </div>
  );
}

// What changed since the modal opened, in the words the grant is made in. A bare count is a
// number you have to take on trust; the list is what makes "3 changes pending" checkable
// before it is saved.
function describeChanges(before, after, modules) {
  const byId = Object.fromEntries(modules.map(m => [m.id, m]));
  const name = id => byId[id]?.label || id;
  const out = [];

  const ids = new Set([...Object.keys(before.modules || {}), ...Object.keys(after.modules || {})]);
  for (const id of ids) {
    const b = (before.modules || {})[id];
    const a = (after.modules || {})[id];
    const m = byId[id];
    if (b === undefined && a !== undefined) out.push(`Share ${name(id)} as ${m ? levelLabel(m, a) : a}`);
    else if (b !== undefined && a === undefined) out.push(`Remove ${name(id)}`);
    else if (b !== a) out.push(`${name(id)}: ${m ? levelLabel(m, b) : b} → ${m ? levelLabel(m, a) : a}`);

    // Client ticks only count as a change while the tool itself is granted — the server drops
    // the rest, and listing them would promise a save that does not happen.
    if (a === undefined) continue;
    // Nor at a level that carries every client: the ticks were dropped rather than saved, so
    // reporting them as a removal would describe a loss of access that is not happening — the
    // level change on the line above is the whole story.
    if (m && grantsAllClients(m, a)) continue;
    const bc = new Set((before.clients || {})[id] || []);
    const ac = (after.clients || {})[id] || [];
    const added   = ac.filter(c => !bc.has(c)).length;
    const removed = [...bc].filter(c => !ac.includes(c)).length;
    if (added || removed) {
      const parts = [];
      if (added)   parts.push(`+${added}`);
      if (removed) parts.push(`−${removed}`);
      out.push(`${name(id)} clients ${parts.join(' ')}`);
    }
  }
  return out;
}

// ── access modal ──────────────────────────────────────────────────────────────

function AccessModal({ token, user, users, catalog, onClose, onSaved }) {
  // Seeded from the row the list already carries, so the modal opens on data rather than on a
  // spinner. GET /portal/users returns both halves for every user for exactly this reason.
  const [access, setAccess] = useState({
    modules: user.modules || {}, clients: user.clients || {},
  });
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState('');
  const [selected, setSel]  = useState(null);
  const [review, setReview] = useState(false);

  const isAdmin = user.role === 'admin';

  // The grant as it stood when the modal opened — the baseline the pending count is measured
  // against, so reverting an edit by hand takes the count back down rather than adding to it.
  const [initial] = useState({ modules: user.modules || {}, clients: user.clients || {} });
  const changes = isAdmin ? [] : describeChanges(initial, access, catalog?.modules || []);

  async function save() {
    setError('');
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/portal/users/${user.id}/access`, {
        method: 'PUT',
        headers: authHeaders(token),
        body: JSON.stringify(access),
      });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed to save access'); return; }
      onSaved(user.id, d);
      onClose();
    } catch {
      setError('Network error');
    } finally {
      setBusy(false);
    }
  }

  const count = Object.keys(access.modules).length;

  return (
    <div className="o-scrim" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="o-modal" style={s.accessModal}>
        <div className="o-modal-head">
          <div style={s.who}>
            <span style={s.avatar} aria-hidden="true">{initials(user)}</span>
            <span style={{ minWidth: 0 }}>
              <span style={s.whoName}>{user.full_name || user.username}</span>
              <span style={s.whoMail}>{user.email || user.username}</span>
            </span>
          </div>
          <div style={s.headActions}>
            {!isAdmin && (
              <CopyFromUser
                users={(users || []).filter(u => u.id !== user.id && u.role !== 'admin')}
                onPick={u => setAccess({ modules: u.modules || {}, clients: u.clients || {} })}
              />
            )}
            <button onClick={onClose} style={s.closeBtn} aria-label="Close">✕</button>
          </div>
        </div>

        {isAdmin ? (
          <div className="o-modal-body">
            <p className="o-hint" style={{ margin: 0 }}><b>Full access.</b> {ADMIN_NOTE}</p>
          </div>
        ) : (
          <AccessPanel
            catalog={catalog} value={access} onChange={setAccess}
            selected={selected} onSelect={setSel}
          />
        )}

        {!isAdmin && count === 0 && (
          <p className="o-hint" style={s.footNote}>
            <b>No tools shared.</b> Saving this leaves the Hub empty for this user.
          </p>
        )}
        {review && changes.length > 0 && (
          <ul style={s.reviewList}>
            {changes.map(c => <li key={c}>{c}</li>)}
          </ul>
        )}
        {error && <div style={{ ...s.errorBox, margin: `0 ${T.s5}px ${T.s3}px` }}>{error}</div>}

        <div className="o-modal-foot" style={s.accessFoot}>
          <span className="o-note">
            {changes.length === 0 ? 'No changes yet' : (
              <>
                {changes.length} change{changes.length === 1 ? '' : 's'} pending{' · '}
                <button type="button" style={s.reviewBtn} onClick={() => setReview(r => !r)}>
                  {review ? 'Hide' : 'Review'}
                </button>
              </>
            )}
          </span>
          <span style={{ display: 'flex', gap: T.s2 }}>
            <button onClick={onClose} className="o-btn">{isAdmin ? 'Close' : 'Cancel'}</button>
            {!isAdmin && (
              <button onClick={save} className="o-btn o-btn-primary" disabled={busy}>
                {busy ? 'Saving…' : 'Save access'}
              </button>
            )}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── field helper ──────────────────────────────────────────────────────────────

function Field({ label, value, onChange, required, type = 'text', compact }) {
  return (
    <div style={s.fieldWrap}>
      <label className="o-field-lbl" style={compact ? s.compactLbl : undefined}>
        {label}{required && ' *'}
      </label>
      <input
        className="o-control"
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        required={required}
      />
    </div>
  );
}

// ── impersonate button ────────────────────────────────────────────────────────

function UserActionIcon({ action }) {
  const paths = {
    edit: 'M12 5l7 7M4 20l4-1L20 7a2.8 2.8 0 0 0-4-4L4 15v5Z',
    access: 'M12 3l8 3v5c0 5-4 8-8 10-4-2-8-5-8-10V6l8-3Zm-4 9 3 3 5-6',
    reset: 'M3 10a9 9 0 1 1 2 9M3 4v6h6M12 8v5l3 2',
    impersonate: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Zm13 0a3 3 0 1 1-6 0 3 3 0 0 1 6 0',
    delete: 'M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7M14 10v7',
  };
  return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d={paths[action]} />
  </svg>;
}

function ImpersonateBtn({ user, me, onError }) {
  const { impersonate } = useAuth();
  const [busy, setBusy] = useState(false);

  // The two cases the server refuses, refused here too so the row explains itself rather than
  // handing back a 403 after a click. `title` carries the reason — the button is disabled, and
  // a disabled control with no explanation is just a dead end.
  const isSelf  = String(user.id) === String(me?.id);
  const isAdmin = user.role === 'admin';
  const why = isSelf ? 'This is you.'
            : isAdmin ? 'Administrators cannot be impersonated — they already see every tool.'
            : '';

  async function go() {
    onError('');
    setBusy(true);
    try {
      await impersonate(user.id);   // navigates on success
    } catch (e) {
      onError(e.message);
      setBusy(false);
    }
  }

  return (
    <button
      onClick={go}
      className="o-iconbtn"
      aria-label="Impersonate" aria-busy={busy}
      disabled={busy || isSelf || isAdmin}
      title={why || `View Orbit as ${user.username}`}
    >
      <UserActionIcon action="impersonate" />
    </button>
  );
}

// ── access summary cell ───────────────────────────────────────────────────────

function AccessSummary({ user, catalog }) {
  if (user.role === 'admin') return <span className="o-badge">All tools</span>;

  const ids = Object.keys(user.modules || {});
  if (!ids.length) return <span style={s.muted}>No tools</span>;

  // Labels, not ids — the row is read by whoever hands out the access, and 'competitive-
  // monitoring' is not what they called it when they decided to. The count carries the rest so
  // a user with six tools does not push the table sideways.
  const byId = Object.fromEntries((catalog?.modules || []).map(m => [m.id, m]));
  const label = id => byId[id]?.label || id;
  const shown = ids.slice(0, 2).map(label);

  // The hover carries the level too, in the tool's own words: two people can both hold the
  // Taxonomy Generator and one of them cannot open the builder.
  const full = ids.map(id => {
    const m = byId[id];
    const lvl = m ? levelLabel(m, user.modules[id]) : user.modules[id];
    return lvl ? `${label(id)} (${lvl})` : label(id);
  }).join(', ');

  return (
    <span title={full}>
      {shown.join(', ')}
      {ids.length > shown.length && (
        <span style={s.muted}> +{ids.length - shown.length}</span>
      )}
    </span>
  );
}

// ── main page ─────────────────────────────────────────────────────────────────

export default function UsersPage() {
  const { user: me } = useAuth();
  const token = localStorage.getItem('mp_token');

  const [users,      setUsers]      = useState([]);
  const [catalog,    setCatalog]    = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');
  const [showAdd,    setShowAdd]    = useState(false);
  const [q,          setQ]          = useState('');
  const [role,       setRole]       = useState('all');
  const [tool,       setTool]       = useState('all');
  const [resetUser,  setResetUser]  = useState(null);
  const [accessUser, setAccessUser] = useState(null);
  const [accountAction, setAccountAction] = useState(null);
  const [canManageAccounts, setCanManageAccounts] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/portal/users`, { headers: authHeaders(token) });
      const d = await r.json();
      if (!r.ok) { setError(d.error || 'Failed to load users'); return; }
      setUsers(d.users);
      setCanManageAccounts(d.canManageAccounts === true);
      setCatalog(d.catalog || null);
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Filtering is client-side: the whole list already arrives in one response, and a round trip
  // per keystroke would make the search slower than the table it filters.
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter(u => {
      if (needle && !`${u.username} ${u.email || ''} ${u.full_name || ''}`
        .toLowerCase().includes(needle)) return false;
      if (role !== 'all' && u.role !== role) return false;
      // An administrator holds every tool by virtue of their role rather than by a grant row,
      // so a tool filter that read only the grants would hide the people with the most access.
      if (tool !== 'all' && u.role !== 'admin' && !(u.modules || {})[tool]) return false;
      return true;
    });
  }, [users, q, role, tool]);

  const filtered = q.trim() || role !== 'all' || tool !== 'all';

  function handleCreated(newUser) {
    setUsers(u => [...u, newUser]);
  }

  // The PUT returns the grants as stored, so the row updates from the server's answer rather
  // than from what was sent — a module admin's write is scoped, and echoing the request would
  // show grants outside that scope as though they had changed.
  function handleAccessSaved(id, saved) {
    setUsers(us => us.map(u => u.id === id
      ? { ...u, modules: saved.modules || {}, clients: saved.clients || {} }
      : u));
  }

  return (
    <div style={s.page}>
      <OrbitModuleHeader title="User Management" />

      <main className="o-page">
        <div className="o-head-row">
          <div>
            <h1 className="o-title">User Management</h1>
            <p className="o-sub">
              {filtered
                ? `${shown.length} of ${users.length} users`
                : `${users.length} user${users.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button onClick={() => setShowAdd(true)} className="o-btn o-btn-primary">+ Add user</button>
        </div>

        {notice && <p className="o-hint" role="status">{notice}</p>}
        {error && <div style={s.errorBox}>{error}</div>}

        <div className="o-ctrl-row o-ctrl-row-tight" style={s.filters}>
          <div className="o-ctrl" style={{ flex: '1 1 260px' }}>
            <label className="o-field-lbl" htmlFor="usr-q">Search</label>
            <input
              id="usr-q" className="o-control" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Username, email or full name"
            />
          </div>
          <div className="o-ctrl">
            <label className="o-field-lbl" htmlFor="usr-role">Role</label>
            <select id="usr-role" className="o-control" value={role}
              onChange={e => setRole(e.target.value)}>
              <option value="all">All roles</option>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="o-ctrl">
            <label className="o-field-lbl" htmlFor="usr-tool">Tool</label>
            <select id="usr-tool" className="o-control" value={tool}
              onChange={e => setTool(e.target.value)}>
              <option value="all">Any tool</option>
              {(catalog?.modules || []).map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>
          {filtered && (
            <button className="o-btn o-btn-sm o-btn-ghost" style={{ alignSelf: 'flex-end' }}
              onClick={() => { setQ(''); setRole('all'); setTool('all'); }}>
              Clear filters
            </button>
          )}
        </div>

        <div className="o-panel" style={{ overflowX: 'auto' }}>
        {loading ? (
          <div className="o-state">Loading…</div>
        ) : users.length === 0 ? (
          <div className="o-state">No users yet.</div>
        ) : shown.length === 0 ? (
          <div className="o-state">
            No user matches these filters. Widen the search or clear them.
          </div>
        ) : (
          <table className="o-tbl">
            <thead>
              <tr>
                {['ID', 'Username', 'Email', 'Full name', 'Role', 'Access', 'Created', 'Actions'].map(h => (
                  <th key={h} className={h === 'Actions' ? 'um-actions-cell' : undefined}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((u, i) => (
                <tr key={u.id} style={{ background: i % 2 === 0 ? T.surface : T.surface2 }}>
                  <td>{u.id}</td>
                  <td className="o-strong">{u.username}</td>
                  <td>{u.email}</td>
                  <td>{u.full_name || <span style={s.muted}>—</span>}</td>
                  <td>
                    <span className={`o-badge ${u.role === 'admin' ? '' : 'o-grey'}`}>
                      {u.role}
                    </span>
                  </td>
                  <td><AccessSummary user={u} catalog={catalog} /></td>
                  <td style={s.muted}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td className="um-actions-cell"><div className="um-user-actions" role="group" aria-label={`Actions for ${u.username}`}>
                    {canManageAccounts && <button className="o-iconbtn" aria-label="Edit" title={`Edit ${u.username}`}
                      onClick={() => setAccountAction({ user: u, deleting: false })}><UserActionIcon action="edit" /></button>}
                    <button onClick={() => setAccessUser(u)} className="o-iconbtn" aria-label="Access" title="Manage access"><UserActionIcon action="access" /></button>
                    <button onClick={() => setResetUser(u)} className="o-iconbtn" aria-label="Reset password" title="Reset password"><UserActionIcon action="reset" /></button>
                    <ImpersonateBtn user={u} me={me} onError={setError} />
                    {canManageAccounts && <button className="o-iconbtn o-iconbtn-danger" aria-label="Delete" disabled={String(u.id) === String(me?.id)}
                      title={String(u.id) === String(me?.id) ? 'You cannot delete your own account.' : `Delete ${u.username}`}
                      onClick={() => setAccountAction({ user: u, deleting: true })}><UserActionIcon action="delete" /></button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        </div>
      </main>

      {accountAction && <UserAccountDialog user={accountAction.user} deleting={accountAction.deleting}
        isSelf={String(accountAction.user.id) === String(me?.id)} onClose={() => setAccountAction(null)}
        onSave={async fields => {
          const response = await fetch(`${API_BASE}/portal/users/${accountAction.user.id}`, {
            method: accountAction.deleting ? "DELETE" : "PATCH", headers: authHeaders(token),
            ...(fields ? { body: JSON.stringify(fields) } : {}),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.error || "The user could not be updated. Try again.");
          setUsers(current => accountAction.deleting ? current.filter(user => user.id !== accountAction.user.id)
            : current.map(user => user.id === result.user.id ? { ...user, ...result.user } : user));
          setNotice(accountAction.deleting ? "User deleted. Their existing work is preserved." : "User updated.");
        }} />}
      {showAdd    && <AddUserModal token={token} users={users} catalog={catalog}
                       onClose={() => setShowAdd(false)} onCreated={handleCreated} />}
      {resetUser  && <ResetModal   token={token} user={resetUser}
                       onClose={() => setResetUser(null)} />}
      {accessUser && <AccessModal  token={token} user={accessUser} users={users} catalog={catalog}
                       onClose={() => setAccessUser(null)} onSaved={handleAccessSaved} />}
    </div>
  );
}

// ── styles ────────────────────────────────────────────────────────────────────

// Layout and spacing only — colour, type and borders come from the shared
// classes in orbit.css wherever the markup could take a className.
const s = {
  page: {
    minHeight: '100vh',
    background: T.paper,
  },
  body: {
    padding: `${T.s6}px ${T.s6}px ${T.s7}px`,
  },
  muted: { color: T.text3 },
  // Its own band above the table rather than inside the panel: the panel is the result, and a
  // filter bar under the same border reads as the first row of it.
  filters: {
    alignItems: 'flex-end', gap: T.s4, marginBottom: T.s3,
    background: T.surface, border: `1px solid ${T.rule}`, borderRadius: T.rMd,
    padding: `10px ${T.s4}px`,
  },
  errorBox: {
    background: T.errorWash,
    border: `1px solid ${T.error}33`,
    borderRadius: T.rSm,
    color: T.error,
    fontSize: T.tSmall,
    padding: `10px ${T.s4}px`,
    marginBottom: T.s4,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '16px',
    color: T.text3,
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: T.s3,
  },
  fieldWrap: {
    display: 'flex',
    flexDirection: 'column',
  },
  pwRow: {
    display: 'flex',
    gap: T.s2,
    alignItems: 'center',
  },
  successBox: {
    background: T.validWash,
    border: `1px solid ${T.valid}33`,
    borderRadius: T.rSm,
    padding: `${T.s3}px ${T.s4}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: T.s2,
  },
  successLine: {
    display: 'flex',
    alignItems: 'center',
    gap: T.s3,
  },
  successLabel: {
    fontSize: T.tLabel,
    fontWeight: 600,
    letterSpacing: '.09em',
    textTransform: 'uppercase',
    color: T.valid,
    width: '80px',
    flexShrink: 0,
  },
  successValue: {
    fontSize: T.tBody,
    color: T.text,
  },
  shareNote: {
    margin: 0,
    fontSize: T.tSmall,
    color: T.text3,
  },
  access: {
    display: 'flex',
    flexDirection: 'column',
    gap: T.s5,
  },
  // ── access split ──
  // Wider than the 540px default because the point of the split is that the tool list and the
  // client list are both readable at once; at 540 one of them is a column of ellipses.
  accessModal: { maxWidth: 'min(820px, 94vw)' },
  who: { display: 'flex', alignItems: 'center', gap: T.s3, minWidth: 0 },
  avatar: {
    // A circle, not a rounded box — the same justification the level pills have: an avatar
    // that is not round does not read as an avatar. '50%' is a shape, not a radius step.
    width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
    background: T.surface2, border: `1px solid ${T.rule}`, color: T.text2,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: T.tLabel, fontWeight: 600, letterSpacing: '.04em',
  },
  whoName: {
    display: 'block', fontSize: T.tBody, fontWeight: 600, color: T.text,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  whoMail: {
    display: 'block', fontSize: T.tSmall, color: T.text3,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  headActions: { display: 'flex', alignItems: 'center', gap: T.s2, flexShrink: 0 },

  split: {
    display: 'grid', gridTemplateColumns: 'minmax(200px, 250px) 1fr',
    flex: '1 1 auto', minHeight: 0,
  },
  // In a form the split cannot take the modal's height, so it takes a fixed one and a border:
  // an unbounded rail would push Create user off the bottom of the body.
  splitEmbedded: {
    flex: '1 1 auto', minHeight: 300, marginTop: T.s1,
    border: `1px solid ${T.rule}`, borderRadius: T.rMd, overflow: 'hidden',
  },
  addBody: { gap: T.s3 },
  addFields: { display: 'grid', gap: T.s2, flex: 'none' },
  compactLbl: { marginBottom: 4 },
  // The grant takes whatever height the identity fields don't, rather than a fixed slice: on a
  // tall window that is most of the modal, which is where the tool list is worth reading.
  toolsField: { display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 },
  toolsHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: T.s2, marginBottom: T.s1, flex: 'none',
  },
  rail: {
    borderRight: `1px solid ${T.rule}`, background: T.paper,
    padding: `${T.s4}px ${T.s3}px`, overflowY: 'auto', minHeight: 0,
  },
  railRow: { display: 'flex', alignItems: 'center', gap: T.s2 },
  railBox: { accentColor: T.ink, width: 15, height: 15, margin: '0 0 0 4px', flexShrink: 0, cursor: 'pointer' },
  railBtn: { flex: 1, minWidth: 0 },
  railName: {
    display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  // The level and client count sit under the name rather than beside it: beside it they are the
  // first thing to be truncated, and a grant whose level you cannot read is not a grant you can
  // check.
  railSub: {
    display: 'block', fontSize: T.tLabel, fontWeight: 400, color: T.text3, marginTop: 1,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  chev: { color: T.text3, flexShrink: 0 },

  detail: {
    padding: `${T.s5}px`, overflowY: 'auto', minHeight: 0,
    display: 'flex', flexDirection: 'column', gap: T.s3,
  },
  detailTitle: { margin: 0, fontSize: T.tBody, fontWeight: 600, color: T.text },
  roleRow: { display: 'flex', alignItems: 'center', gap: T.s3 },
  clientHead: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: T.s2,
  },
  clientList: { display: 'flex', flexDirection: 'column', gap: 0 },
  // Stacked flush rather than as separate cards: the list is one set to scan down, and eight
  // gapped boxes read as eight decisions instead of one.
  clientRow: { borderRadius: 0, marginTop: -1 },

  copyAway: { position: 'fixed', inset: 0, zIndex: 55 },
  copyPop: { position: 'fixed', width: 260 },
  copyList: { maxHeight: 220, overflowY: 'auto' },

  // In flow rather than overlaid: the modal body scrolls, and CopyFromUser's fixed-position
  // escape hatch is not worth repeating when the results can simply sit under the input.
  dirList:  { position: 'static', width: '100%', maxHeight: 240, overflowY: 'auto', marginTop: T.s1 },
  dirTaken: { opacity: .55, cursor: 'default' },
  dirBadge: { marginLeft: T.s2 },

  footNote: { margin: `0 ${T.s5}px ${T.s3}px` },
  reviewList: {
    margin: `0 ${T.s5}px ${T.s3}px`, padding: `${T.s2}px ${T.s4}px`,
    listStyle: 'none', background: T.surface2, border: `1px solid ${T.rule}`,
    borderRadius: T.rSm, fontSize: T.tSmall, color: T.text2, maxHeight: 132, overflowY: 'auto',
  },
  accessFoot: {
    justifyContent: 'space-between', alignItems: 'center',
    borderTop: `1px solid ${T.ruleSoft}`, background: T.surface2, paddingTop: T.s3,
  },
  reviewBtn: {
    border: 0, background: 'none', padding: 0, font: 'inherit', color: T.text2,
    textDecoration: 'underline', cursor: 'pointer',
  },
};
